import { Progress } from '../ui/Progress';
import { Text } from '../ui/Text';

interface PasswordStrengthMeterProps {
  password: string;
}

interface PasswordValidation {
  isValid: boolean;
  errors: string[];
  strength: 0 | 1 | 2 | 3 | 4;
}

function validatePasswordStrength(password: string): PasswordValidation {
  const errors: string[] = [];
  let strength = 0;

  if (!password) {
    return { isValid: false, errors: ['Password is required'], strength: 0 };
  }

  const hasLowercase = /[a-z]/.test(password);
  const hasUppercase = /[A-Z]/.test(password);
  const hasNumber = /\d/.test(password);
  const hasSpecial = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password);
  const hasMinLength = password.length >= 12;
  const hasMaxLength = password.length <= 128;

  if (!hasMinLength) {
    errors.push('Password must be at least 12 characters long');
  } else {
    strength++;
  }

  if (!hasMaxLength) {
    errors.push('Password must be less than 128 characters');
  }

  if (!hasLowercase) {
    errors.push('Password must contain at least one lowercase letter');
  } else {
    strength++;
  }

  if (!hasUppercase) {
    errors.push('Password must contain at least one uppercase letter');
  } else {
    strength++;
  }

  if (!hasNumber) {
    errors.push('Password must contain at least one number');
  } else {
    strength++;
  }

  if (!hasSpecial) {
    errors.push('Password must contain at least one special character');
  } else {
    strength++;
  }

  if (hasMinLength && hasMaxLength && hasLowercase && hasUppercase && hasNumber && hasSpecial) {
    strength = 4;
  }

  return {
    isValid: errors.length === 0,
    errors,
    strength: Math.min(strength, 4) as 0 | 1 | 2 | 3 | 4,
  };
}

export function PasswordStrengthMeter({ password }: PasswordStrengthMeterProps) {
  const validation = validatePasswordStrength(password);

  const strengthColors = {
    0: 'bg-red-500',
    1: 'bg-orange-500',
    2: 'bg-yellow-500',
    3: 'bg-blue-500',
    4: 'bg-green-500',
  };

  const strengthLabels = {
    0: 'Very Weak',
    1: 'Weak',
    2: 'Fair',
    3: 'Good',
    4: 'Strong',
  };

  const progressValue = (validation.strength / 4) * 100;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Text size="sm" className="text-gray-600 dark:text-gray-400">
          Password Strength
        </Text>
        <Text
          size="sm"
          weight="medium"
          className={validation.strength >= 3 ? 'text-green-600' : 'text-orange-600'}
        >
          {strengthLabels[validation.strength]}
        </Text>
      </div>

      <Progress
        value={progressValue}
        className="h-2"
        indicatorClassName={strengthColors[validation.strength]}
      />

      {password && validation.errors.length > 0 && (
        <ul className="space-y-1 mt-2">
          {validation.errors.map((error, index) => (
            <li key={index}>
              <Text size="xs" className="text-red-600 dark:text-red-400">
                • {error}
              </Text>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
